import React from 'react';
import { Grid, TextField } from '@mui/material';
import { Field, FieldProps } from 'formik';
import Autocomplete from '@mui/material/Autocomplete';
import { addDaysToInputDate } from '../../../../shared/utils/helpers/date';
import { PRAZO_ENTREGA_PADRAO_DIAS } from '../../../../hooks/useOrderServicoCadastrar';

interface OrdemServicoFormProps {
  errors: any;
  touched: any;
  isTryingToAdvance: boolean;
  usuarios: any[];
  clientes: any[];
  setUsuarioSelecionado: (usuario: any) => void;
  setClienteSelecionado: (cliente: any) => void;
  duploClick: boolean;
  edicaoRestrita: boolean;
}

// edicaoRestrita: OS em status A Faturar / Reprovado / Finalizado -> apenas
// Data de Entrega e Descricao ficam liberadas; o resto do formulario segue
// bloqueado. Nos demais status a tela e editavel por inteiro.
const OrdemServicoCadastro: React.FC<OrdemServicoFormProps> = ({
  errors,
  touched,
  isTryingToAdvance,
  usuarios,
  clientes,
  duploClick,
  setUsuarioSelecionado,
  setClienteSelecionado,
  edicaoRestrita,
}) => (
  <Grid
    container
    spacing={2}
    sx={{ width: '80%', margin: '0 auto' }}
  >
    <Grid item xs={4}>
      <fieldset disabled={duploClick || edicaoRestrita}>
        <Field
          disabled={true}
          name='os_id'
          as={TextField}
          label='Número OS'
          type='text'
          fullWidth
          InputProps={{
            readOnly: true,
          }}
          error={Boolean(errors?.os_id)}
          helperText={errors?.os_id}
        />
      </fieldset>
    </Grid>

    <Grid item xs={4}>
      <fieldset disabled={duploClick || edicaoRestrita}>
        <Field name='os_data_emissao'>
          {({ field, form }: FieldProps) => (
            <TextField
              {...field}
              label='Data de Emissão'
              type='date'
              fullWidth
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const novaEmissao = event.target.value;
                form.setFieldValue('os_data_emissao', novaEmissao);

                // Projeta a entrega 15 dias apos a emissao escolhida.
                const entregaProjetada = addDaysToInputDate(novaEmissao, PRAZO_ENTREGA_PADRAO_DIAS);
                if (entregaProjetada) {
                  form.setFieldValue('os_dataEntrega', entregaProjetada);
                }
              }}
              error={Boolean(errors?.os_data_emissao)}
              helperText={errors?.os_data_emissao}
            />
          )}
        </Field>
      </fieldset>
    </Grid>

    <Grid item xs={4}>
      {/* Data de Entrega segue editavel tambem no modo restrito. */}
      <fieldset disabled={duploClick}>
        <Field
          name='os_dataEntrega'
          as={TextField}
          label='Data de Entrega'
          type='date'
          fullWidth
          error={Boolean(errors?.os_dataEntrega)}
          helperText={errors?.os_dataEntrega}
        />
      </fieldset>
    </Grid>

    <Grid item xs={12}>
      <fieldset disabled={duploClick || edicaoRestrita}>
        <Field name='os_responsavelId'>
          {({ form }: FieldProps) => (
            <Autocomplete
              options={usuarios}
              getOptionLabel={(option) => option?.cli_nome || ''}
              value={usuarios.find((u) => u.cli_id === form.values?.os_responsavelId) || null}
              onChange={(_, newValue) => {
                form.setFieldValue('os_responsavelId', newValue?.cli_id || '');
              }}
              onBlur={() => form.setFieldTouched('os_responsavelId', true, true)}
              onInputChange={(_, newInputValue) => {
                setUsuarioSelecionado({ cli_nome: newInputValue });
              }}
              isOptionEqualToValue={(option, value) =>
                option.usu_id === value?.cli_id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Responsável"
                  error={(touched.os_responsavelId || isTryingToAdvance) && Boolean(errors.os_responsavelId)}
                  helperText={(touched.os_responsavelId || isTryingToAdvance) ? errors.os_responsavelId : ''}
                />
              )}
            />
          )}
        </Field>
      </fieldset>
    </Grid>

    <Grid item xs={12}>
      <fieldset disabled={duploClick || edicaoRestrita}>
        <Field name='os_cliId'>
          {({ form }: FieldProps) => (
            <Autocomplete
              options={clientes}
              getOptionLabel={(option) => option?.cli_nome || option?.cli_razao || ''}
              value={clientes.find((c) => c.cli_id === form.values?.os_cliId) || null}
              onChange={(_, newValue) => {
                form.setFieldValue('os_cliId', newValue?.cli_id || '');
              }}
              onBlur={() => form.setFieldTouched('os_cliId', true, true)}
              onInputChange={(_, newInputValue) => {
                setClienteSelecionado({ cli_nome: newInputValue });
              }}
              filterOptions={(options, params) => {
                const input = params.inputValue.toLowerCase();
                return options.filter(
                  (opt) =>
                    opt.cli_nome?.toLowerCase().includes(input) ||
                    opt.cli_razao?.toLowerCase().includes(input)
                );
              }}
              isOptionEqualToValue={(option, value) =>
                option.cli_id === value?.cli_id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Clientes"
                  error={(touched.os_cliId || isTryingToAdvance) && Boolean(errors.os_cliId)}
                  helperText={(touched.os_cliId || isTryingToAdvance) ? errors.os_cliId : ''}
                />
              )}
            />
          )}
        </Field>
      </fieldset>
    </Grid>

    <Grid item xs={12}>
      {/* Descricao segue editavel tambem no modo restrito. */}
      <fieldset disabled={duploClick}>
        <Field name='os_descricao'>
          {({ field, form }: FieldProps) => (
            <TextField
              {...field}
              label='Descrição'
              type='text'
              fullWidth
              multiline
              inputProps={{ maxLength: 1000 }}
              rows={5}
              error={Boolean(errors?.os_descricao)}
              helperText={
                errors?.os_descricao
                  ? errors.os_descricao
                  : `${form.values?.os_descricao ? form.values.os_descricao.length : 0}/1000`
              }
            />
          )}
        </Field>
      </fieldset>
    </Grid>
  </Grid>
);

export default OrdemServicoCadastro;
